import { isEmpty, isNil } from 'lodash';
import { RETURN_CHANNEL_SLUG_QUERY } from '@shared/scenes/cards/form/update/constants';
import { RouteParamMapping, WebPathMapping } from './routeParams';

function prepareJSONparams(object: Record<string, any>) {
  return JSON.stringify(object);
}

export const browserRouteMapping: Partial<WebPathMapping<RouteParamMapping>> = {
  Home: () => `/`,
  CreateAdditionalBusiness: () => `/newbusiness`,
  BaseChannelsTab: () => `/channels`,
  Channel: ({ channelSlug, selectedMessage }) =>
    !!selectedMessage
      ? `/${channelSlug}/messages/${selectedMessage.id}/${selectedMessage.createdAt}`
      : `/${channelSlug}`,
  ChannelInfos: ({ channelSlug }) => `/${channelSlug}/cards/infos`,
  AdvancedChannelOptions: ({ channelSlug }) => `/${channelSlug}/cards/infos/advanced`,
  ChannelEdit: ({ channelSlug, autoFocus }) => {
    let path = `/${channelSlug}/cards/infos/edit`;
    if (autoFocus) {
      path += `?autoFocus=${autoFocus}`;
    }
    return path;
  },
  WorkspaceTrash: ({ channelSlug }) => `/${channelSlug}/cards/trash`,
  ChannelUserList: ({ channelSlug }) => `/${channelSlug}/members`,
  ChannelNotificationSettings: ({ channelSlug }) => `/${channelSlug}/notifications`,
  ChannelAddPeople: ({ channelSlug }) => `/${channelSlug}/add`,
  ChannelCreateShareLink: ({ channelSlug }) => `/${channelSlug}/cards/infos/share-link`,
  ChannelChat: ({ channelSlug }) => `/${channelSlug}`,
  ChannelCardList: ({ channelSlug, templateSlug }) =>
    `/${channelSlug}/cards${!!templateSlug ? `/t/${templateSlug}` : ''}`,
  WorkspaceSectionAddPeople: ({ workspaceSectionId }) => `/section/${workspaceSectionId}/add`,
  ChannelFragmentTemplatesList: ({ channelSlug }) => `/${channelSlug}/fragment-templates`,
  ChannelFragmentTemplatesEdit: ({ channelSlug, templateId }) =>
    `/${channelSlug}/fragment-templates/${templateId}`,
  ChannelFragmentTemplatesCreate: ({ channelSlug }) => `/${channelSlug}/fragment-templates/create`,
  CardCreate: ({
    channelSlug,
    templateSlug,
    fieldOverrides,
    cardDefinitionId,
    restoreSavedData,
    fromEntity,
  }) => {
    let path = `/${channelSlug}/cards/create`;
    if (!isNil(templateSlug)) {
      path += `/t/${templateSlug}`;
    }
    if (!isNil(cardDefinitionId)) {
      path += `/c/${cardDefinitionId}`;
    }
    const urlParams = {};
    if (!isNil(fieldOverrides)) {
      urlParams['fields'] = prepareJSONparams(fieldOverrides);
    }
    if (!isNil(fromEntity)) {
      urlParams['fromEntity'] = fromEntity;
    }
    if (!isNil(restoreSavedData)) {
      urlParams['restoreSavedData'] = 'true';
    }

    if (!isEmpty(urlParams)) {
      const query = new URLSearchParams(urlParams);
      path += `/?${query.toString()}`;
    }
    return path;
  },
  CardDetails: ({ cardId, channelSlug, templateSlug, fromAllCards }) =>
    fromAllCards
      ? `/all-cards/${channelSlug}/${cardId}`
      : !!templateSlug
      ? `/${channelSlug}/cards/${cardId}/t/${templateSlug}`
      : `/${channelSlug}/cards/${cardId}`,
  CardDetailsNoAccess: ({ channelSlug }) => `/${channelSlug}/cards/no-access`,
  CardHistory: ({ cardId, channelSlug }) => `/${channelSlug}/cards/${cardId}/history`,
  CardUpdate: ({
    cardId,
    channelSlug,
    templateSlug,
    fieldOverrides,
    restoreSavedData,
    returnChannelSlug,
    returnCardId,
  }) => {
    let path = `/${channelSlug}/cards/${cardId}/update`;
    if (!!templateSlug) {
      path += `/t/${templateSlug}`;
    }
    const urlParams = {};
    if (!isNil(fieldOverrides)) {
      urlParams['fields'] = prepareJSONparams(fieldOverrides);
    }
    if (!isNil(restoreSavedData)) {
      urlParams['restoreSavedData'] = 'true';
    }
    if (!isNil(returnChannelSlug)) {
      urlParams[RETURN_CHANNEL_SLUG_QUERY] = returnChannelSlug;
    }
    if (!isNil(returnCardId)) {
      urlParams['returnCardId'] = returnCardId;
    }

    if (!isEmpty(urlParams)) {
      const query = new URLSearchParams(urlParams);
      path += `/?${query.toString()}`;
    }

    return path;
  },
  CardBulkUpdate: ({
    channelSlug,
    cardId,
    workflowTemplateId,
    recurringEntitiesScheduleId,
    allOrUpcoming,
  }) => {
    return `/${channelSlug}/cards/${cardId}/update/${workflowTemplateId}/bulk/${recurringEntitiesScheduleId}/${allOrUpcoming}`;
  },
  NewMessageContainer: () => `/channel/create`,
  MessageStart: () => `/channel/create`,
  MessageGroupCreate: () => `/channel/create/new`,
  MessageGroupName: () => `/channel/create/details`,
  CreateChannelInviteContacts: () => `/channel/create/new`,
  CreateChannelWorkflowLibrary: () => `/channel/create/workflow`,
  // Bundle channel creation routes (formerly app listing)
  BundleInstallSetup: ({ bundleId }) => `/bundle/install/${bundleId}`,
  BundleInstallAddPeople: ({ bundleId }) => `/bundle/install/${bundleId}/people`,
  BundleInstallDetails: ({ bundleId }) => `/bundle/install/${bundleId}/details`,
  Login: () => `/signup`,
  LoginExistingAccount: ({ error } = {}) => (isNil(error) ? '/signin' : `/signin?error=${error}`),
  LoginVerificationCode: () => `/verify`,
  LoginBusiness: () => `/signin/biz`,
  LoginBusinessUserList: () => `/auth`,
  LoginBusinessUserCreate: () => `/auth/user/create`,
  LoginSamlAuthentication: ({ code }) => `/saml-auth?code=${code}`,
  OnboardingProfile: () => `/onboarding/profile`,
  OnboardingCreateOrJoinBusiness: () => `/onboarding/createOrJoin`,
  OnboardingCreateBusiness: () => `/onboarding/business`,
  OnboardingSelectWorkflows: () => `/onboarding/workflows`,
  OnboardingCreateDirectMessages: () => `/onboarding/dms`,
  OnboardingLastStep: () => `/onboarding/lastStep`,
  OnboardingInviteContacts: () => `/onboarding/invite`,
  OnboardingDone: () => `/onboarding/done`,
  OnboardingBundleInstall: ({ bundleId }) => `/onboarding/bundle/${bundleId}`,
  BaseCardsTab: () => `/all-cards`,
  DashboardTab: () => '/dashboard',
  ReportingTab: () => '/reports',
  Report: ({ workflowTemplateId, reportId }) => `/reports/${workflowTemplateId}/${reportId}`,
  WorkflowEdit: ({ channelSlug, workflowId, queryString }) =>
    isNil(queryString)
      ? `/${channelSlug}/workflows/${workflowId}/edit`
      : `/${channelSlug}/workflows/${workflowId}/edit?originWorkflowTemplateId=${queryString.originWorkflowTemplateId}&originComponentId=${queryString.originComponentId}`,
  WorkflowCreate: ({ channelSlug, queryString }) => {
    return `/${channelSlug}/workflows/create?originWorkflowTemplateId=${queryString.originWorkflowTemplateId}&originComponentId=${queryString.originComponentId}&name=${queryString.name}`;
  },
  WorkflowUpload: ({ channelSlug, queryString }) => {
    return `/${channelSlug}/workflows/upload?originWorkflowTemplateId=${queryString.originWorkflowTemplateId}&originComponentId=${queryString.originComponentId}&name=${queryString.name}`;
  },
  SharedCardLinkViewTemplate: ({ channelSlug, viewTemplateId }) =>
    `/${channelSlug}/sharedLinks/viewTemplates/${viewTemplateId}`,
  SubformsList: ({ channelSlug }) => `/${channelSlug}/subforms`,
  Playground: ({ tab }) => {
    if (isNil(tab)) {
      return '/playground';
    } else {
      return `/playground/${tab}`;
    }
  },
};

export const megaModalRouteMapping: Partial<WebPathMapping<RouteParamMapping>> = {
  ProfileEdit: () => '/profile',
  NotificationSettings: () => '/notifications',
  BaseToolsTab: () => '/links',
  LinkCreate: () => '/links/create',
  Billing: () => '/billing',
  BusinessEdit: () => '/business',
  BusinessSettings: () => '/settings',
  BulkUpdateQueue: () => '/bulk-update-queue',
  Trash: () => '/trash',
  Directory: () => '/directory',
  DirectoryProfile: ({ userId }) => `/directory/user/${Number(userId)}`,
  UserGroups: () => '/user-groups',
  UserGroupsProfile: ({ groupId }) => `/user-groups/group/${groupId}`,
  InviteContacts: () => '/invite',
};
